import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';
import Link from '@material-ui/core/Link';
import Button from '@material-ui/core/Button';
import LinkIcon from '@material-ui/icons/Link';
import Typography from '@material-ui/core/Typography';
import api from '~/services';
import * as lib from '~/dependencies';
import messagesToast from '~/validations/messagesNotifications';
import { openExternalURL, integrationConfig } from '~/helpers';
import { Accordion, AccordionSummary, AccordionDetails } from './styles';

const { AUTO_TASK } = integrationConfig;

const endpointName = {
  18: 'zendesk/tickets/link-call',
  56: 'auto_task/tickets/link_call',
};

export default function ListTickets({ tickets, onTicketLinkedToCall }) {
  const [expanded, setExpanded] = useState(null);
  const [ticketInfo, setTicketInfo] = useState({});

  const toggleExpand = ticketId => (event, newExpanded) => {
    setExpanded(newExpanded ? ticketId : null);
  };

  useEffect(() => {
    const source = lib.axios.CancelToken.source();

    // Associate ticket to call ID
    const associateToCallId = async () => {
      try {
        if (ticketInfo?.ticket_id) {
          const { ticket_id, callId, integrationId } = ticketInfo;

          const response = await api.integrations.post.byDynamicURI(
            `${endpointName[integrationId]}`,
            {
              ticket_id,
              call_id: callId,
            },
            {
              cancelToken: source.token,
            }
          );

          if (response.data?.success) {
            setTicketInfo({});
            onTicketLinkedToCall();
            lib.ToastMessage(
              'success',
              messagesToast().INTEGRATIONS.tickets.onLinkedSuccess
            );
          }
        }
      } catch (err) {
        lib.ToastMessage('error', `Failure to link ticket! Reason: ${err}`);
      }
    };

    associateToCallId();

    return () => source.cancel('Axios request canceled.');
  }, [onTicketLinkedToCall, ticketInfo]);

  return (
    <div>
      {tickets?.length ? (
        <>
          {lib._.map(tickets, t => {
            return (
              <Accordion
                square
                key={t.ticket_id}
                expanded={expanded === t.ticket_id}
                onChange={toggleExpand(t.ticket_id)}>
                <AccordionSummary
                  aria-controls="panel-content"
                  id={t.ticket_id}>
                  <Typography>{t.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={0}>
                    <Grid item xs={12} md={6}>
                      <div>
                        <List dense>
                          <ListItem>
                            <ListItemText
                              size="small"
                              primary="Ticket Number:"
                              secondary={
                                <Link
                                  href={() => false}
                                  variant="body2"
                                  onClick={() => openExternalURL(t.detail_url)}>
                                  {t.integrationId === AUTO_TASK
                                    ? t.ticket_number
                                    : t.ticket_id}
                                </Link>
                              }
                            />
                          </ListItem>
                          {t.statusLabel && (
                            <ListItem>
                              <ListItemText
                                primary="Status:"
                                secondary={t.statusLabel}
                              />
                            </ListItem>
                          )}
                          {t.priorityLabel && (
                            <ListItem>
                              <ListItemText
                                primary="Priority:"
                                secondary={t.priorityLabel}
                              />
                            </ListItem>
                          )}
                          {t.statusLabel && (
                            <ListItem>
                              <ListItemText
                                primary="Queue:"
                                secondary={t.queueLabel}
                              />
                            </ListItem>
                          )}
                          <ListItem>
                            <ListItemText
                              primary="Created At:"
                              secondary={t.created_at}
                            />
                          </ListItem>
                          {t.due_at && (
                            <ListItem>
                              <ListItemText
                                primary="Due At:"
                                secondary={t.due_at}
                              />
                            </ListItem>
                          )}
                          <ListItem>
                            <TextField
                              id="ticket-description"
                              value={t.description || ''}
                              style={{ marginTop: 15 }}
                              label="Description"
                              variant="outlined"
                              multiline
                              rows={4}
                              fullWidth
                              disabled
                            />
                          </ListItem>
                          {t.linked_to_this_call ? (
                            <ListItem
                              style={{
                                justifyContent: 'center',
                                marginTop: 15,
                              }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<LinkIcon />}
                                disabled>
                                Link To This Call
                              </Button>
                            </ListItem>
                          ) : (
                            <ListItem
                              style={{
                                justifyContent: 'center',
                                marginTop: 15,
                              }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<LinkIcon />}
                                disabled={!!ticketInfo?.ticket_id}
                                onClick={() => setTicketInfo(t)}>
                                Link To This Call
                              </Button>
                            </ListItem>
                          )}
                        </List>
                      </div>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      ) : (
        <>
          <Grid container spacing={0}>
            <Grid
              item
              xs={12}
              md={6}
              style={{
                fontSize: 16,
                paddingTop: 10,
                paddingBottom: 10,
                backgroundColor: '#FFFFE0',
                textAlign: 'center',
              }}>
              There are no open tickets available for this contact.
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
}

ListTickets.propTypes = {
  tickets: lib.PropTypes.instanceOf(Object).isRequired,
  onTicketLinkedToCall: lib.PropTypes.func.isRequired,
};
